import type { ClassValue } from 'clsx'
import {
  BookOpenTextIcon,
  GearIcon,
  ShieldWarningIcon,
} from '@phosphor-icons/react'
import { clsx } from 'clsx'
import { extendTailwindMerge } from 'tailwind-merge'

import { ModuleTypeEnum } from '@scrambl/shared'

import { createLocalIcon } from '../components/local-icon'

// Teach tailwind-merge the theme's custom scales (tailwind.config.js), so a
// later class replaces an earlier one of the same kind instead of both
// surviving with CSS order picking the winner. Without `spacing.sheet`,
// SplitView's `cn(FLOW_VIEWPORT, 'mx-0')` kept the sheet gutter, insetting
// every split-view table 12px inside its white sheet; without the radius
// keys, `cn('rounded-full', 'rounded-control')` left the avatar round.
const twMerge = extendTailwindMerge({
  extend: {
    theme: {
      spacing: ['sheet'],
      borderRadius: [
        'mark',
        'chip',
        'cell',
        'control',
        'inset',
        'card',
        'shell',
        'pill',
      ],
    },
  },
})

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Check if a URL is already resolved (signed S3 URL, CDN URL, blob, or data URL)
 * These URLs can be used directly without needing to fetch a signed URL from the API
 */
export function isResolvedUrl(url: string | null | undefined): boolean {
  if (!url) return false

  // Blob and data URLs
  if (url.startsWith('blob:') || url.startsWith('data:')) {
    return true
  }

  // S3 signed URLs (amazonaws.com)
  if (url.includes('amazonaws.com')) {
    return true
  }

  // CloudFront CDN URLs (*.cloudfront.net)
  if (url.includes('cloudfront.net')) {
    return true
  }

  // Full HTTP(S) URLs that are not S3 keys
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return true
  }

  return false
}

// Add debounce utility function
export function debounce<T extends (...args: unknown[]) => void>(
  func: T,
  wait: number,
): T & { cancel: () => void } {
  let timeout: ReturnType<typeof setTimeout> | undefined

  const debounced = (...args: Parameters<T>) => {
    const later = () => {
      timeout = undefined
      func(...args)
    }

    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }

  debounced.cancel = () => {
    clearTimeout(timeout)
  }

  return debounced as T & { cancel: () => void }
}

// Get module icon based on module type - web-specific functionality
export function getModuleIcon(moduleType: ModuleTypeEnum | string) {
  switch (moduleType) {
    case ModuleTypeEnum.PROJECT:
      return createLocalIcon('folder-outline')
    case ModuleTypeEnum.TASK:
      return createLocalIcon('checkbox-outline')
    case ModuleTypeEnum.TICKET:
      return createLocalIcon('ticket-outline')
    case ModuleTypeEnum.ENTITY:
      return createLocalIcon('building-outline')
    case ModuleTypeEnum.DEAL:
      return createLocalIcon('deal-outline')
    case ModuleTypeEnum.CONTACT:
      // Same glyph as the Contacts sidebar item ('call-outline' is a funnel).
      return createLocalIcon('person-outline')
    case ModuleTypeEnum.EVENT:
      return createLocalIcon('megaphone-outline')
    case ModuleTypeEnum.CONTRACT:
      return createLocalIcon('contract-outline')
    case ModuleTypeEnum.INVOICE:
      return createLocalIcon('receipt-outline')
    case ModuleTypeEnum.QUOTE:
      return createLocalIcon('receipt-outline')
    case ModuleTypeEnum.FINANCIALS:
      return createLocalIcon('trending-up-outline')
    case ModuleTypeEnum.THREAD:
      return createLocalIcon('chatbubble-outline')
    case ModuleTypeEnum.TIME_ENTRY:
      return createLocalIcon('time-outline')
    case ModuleTypeEnum.MEMBER:
      return createLocalIcon('people-outline')
    case ModuleTypeEnum.DOCUMENT:
      return createLocalIcon('document-text-outline')
    case ModuleTypeEnum.ORGANIZATION:
      return GearIcon
    case ModuleTypeEnum.CUSTOM_COLLECTION:
      return createLocalIcon('list-outline')
    case ModuleTypeEnum.KNOWLEDGE_BASE:
      return BookOpenTextIcon
    default:
      return ShieldWarningIcon
  }
}
